import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Package, Mail, Check, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Helper to get user-friendly error messages for magic link failures
const getMagicLinkErrorMessage = (errorCode: string | null, errorDescription: string | null): { title: string; message: string; canRetry: boolean } => {
  const code = errorCode?.toLowerCase() || '';
  const desc = errorDescription?.toLowerCase() || '';

  if (code.includes('expired') || desc.includes('expired') || code === 'otp_expired') {
    return {
      title: "Link Expired",
      message: "This magic link has expired. Links are valid for 1 hour. Please request a new one.",
      canRetry: true
    };
  }

  if (code.includes('already') || desc.includes('already') || code === 'otp_disabled' || desc.includes('used')) {
    return {
      title: "Link Already Used",
      message: "This magic link has already been used. Each link can only be used once. Please request a new one.",
      canRetry: true
    };
  }

  if (code.includes('invalid') || desc.includes('invalid') || code === 'bad_jwt') {
    return {
      title: "Invalid Link",
      message: "This magic link is invalid or malformed. Please request a new one.",
      canRetry: true
    };
  }

  if (code.includes('rate') || desc.includes('rate') || code === 'over_request_rate_limit') {
    return {
      title: "Too Many Attempts",
      message: "You've requested too many magic links. Please wait a few minutes before trying again.",
      canRetry: false
    };
  }

  if (code.includes('user_not_found') || desc.includes('user not found')) {
    return {
      title: "Account Not Found",
      message: "No account exists with this email. Please sign up first.",
      canRetry: true
    };
  }

  return {
    title: "Sign In Failed",
    message: errorDescription || "Something went wrong with your magic link. Please request a new one.",
    canRetry: true
  };
};

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [step, setStep] = useState<"email" | "sent" | "verified" | "processing">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; message: string; canRetry: boolean } | null>(null);
  const [showRetry, setShowRetry] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const hasProcessedToken = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const errorCode = hashParams.get('error_code');
    const errorDescription = hashParams.get('error_description');
    const urlError = searchParams.get('error');
    const urlErrorDescription = searchParams.get('error_description');

    if (errorCode || urlError) {
      console.log('[Auth] Magic link error:', errorCode || urlError, errorDescription || urlErrorDescription);
      const errorInfo = getMagicLinkErrorMessage(errorCode || urlError, errorDescription || urlErrorDescription);
      setError(errorInfo);
      setStep("email");
      setShowMagicLink(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (accessToken && refreshToken && !hasProcessedToken.current) {
      hasProcessedToken.current = true;
      console.log('[Auth] Tokens detected in URL, showing processing state');
      setStep("processing");
      setShowRetry(false);
      
      timeoutRef.current = setTimeout(() => {
        console.log('[Auth] Timeout reached, showing retry button');
        setShowRetry(true);
      }, 10000);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] Auth state change:', event, session?.user?.email);
      
      if (event === 'SIGNED_IN' && session) {
        console.log('[Auth] SIGNED_IN event received, navigating to dashboard');
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowRetry(false);
        setStep("verified");
        
        if (window.location.hash) {
          window.history.replaceState({}, '', window.location.pathname);
        }
        
        toast({
          title: "Welcome to Endless Storage!",
          description: "You're now logged in."
        });

        setTimeout(() => navigate('/dashboard'), 500);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[Auth] Initial session check:', session?.user?.email);
      if (session) {
        navigate('/dashboard');
      }
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [navigate, toast, searchParams]);

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/auth`;
      
      const { data, error } = await supabase.functions.invoke('send-auth-magic-link', {
        body: { email, redirectTo: redirectUrl }
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      setStep("sent");
      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link."
      });
    } catch (err: any) {
      console.error('Auth error:', err);
      setError({ title: "Failed to Send", message: err.message || 'Failed to send magic link. Please try again.', canRetry: true });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/auth`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Google sign in error:', err);
      setError({ title: "Google Sign In Failed", message: err.message || 'Failed to sign in with Google. Please try again.', canRetry: true });
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      
      // Navigate immediately on successful sign-in
      if (data.session) {
        toast({
          title: "Welcome back!",
          description: "You're now logged in."
        });
        navigate('/dashboard');
        return;
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError({ title: "Sign In Failed", message: err.message || 'Failed to sign in. Please try again.', canRetry: true });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    if (password.length < 6) {
      setError({ title: "Password Too Short", message: 'Password must be at least 6 characters.', canRetry: true });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });
      if (signUpError) throw signUpError;

      if (signUpData.user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.functions.invoke('send-signup-welcome', {
            body: {
              email,
              name: email.split('@')[0]
            }
          });
        }
      }

      const { error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (loginError) throw loginError;
      toast({
        title: "Account created!",
        description: "You're now logged in."
      });
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError({ title: "Sign Up Failed", message: err.message || 'Failed to create account. Please try again.', canRetry: true });
    } finally {
      setLoading(false);
    }
  };

  const isSignup = activeTab === "signup";

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Package className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary">Endless Storage</span>
          </div>
          <p className="text-muted-foreground">Organizing Your Home Made Simple</p>
        </div>

        <Card className="shadow-elegant">
          <CardHeader className="pb-4">
            {step === "email" && (
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "signin" | "signup"); setError(null); setShowMagicLink(false); }} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin" className="text-base">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" className="text-base">Create Account</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            {step !== "email" && (
              <>
                <CardTitle>
                  {step === "sent" && "Check Your Email"}
                  {step === "processing" && "Signing You In..."}
                  {step === "verified" && "You're In!"}
                </CardTitle>
                <CardDescription>
                  {step === "sent" && "We've sent you a magic link"}
                  {step === "processing" && "Please wait while we verify your login"}
                  {step === "verified" && "Redirecting to your dashboard..."}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{error.title}</strong>
                  <p className="mt-1">{error.message}</p>
                  {error.canRetry && !showMagicLink && (
                    <Button 
                      variant="link" 
                      className="p-0 h-auto mt-2 text-destructive-foreground underline"
                      onClick={() => {
                        setError(null);
                        setShowMagicLink(true);
                      }}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Request a magic link instead
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {step === "email" && (
              <>
                {showMagicLink ? (
                  <form onSubmit={handleSendMagicLink} className="space-y-4">
                    <Input 
                      type="email" 
                      placeholder="Email address" 
                      value={email} 
                      onChange={e => setEmail(e.target.value)} 
                      required 
                      disabled={loading} 
                      className="h-12 text-base" 
                    />
                    <Button type="submit" className="w-full h-12 bg-gradient-primary text-base" disabled={loading}>
                      {loading ? "Sending..." : "Send magic link"}
                    </Button>
                    <div className="bg-muted/50 rounded-lg p-3 mt-2">
                      <p className="text-sm text-muted-foreground text-center">
                        📬 Check your inbox (and spam folder) for a secure sign-in link. It may take up to a minute to arrive.
                      </p>
                    </div>
                    <button 
                      type="button" 
                      className="w-full text-sm text-primary hover:underline"
                      onClick={() => setShowMagicLink(false)}
                    >
                      ← Back to {isSignup ? "create account" : "sign in"}
                    </button>
                  </form>
                ) : (
                  <>
                    {/* Google sign-in */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 text-base mb-4"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      Continue with Google
                    </Button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">or</span>
                      </div>
                    </div>

                    {/* Email/Password form */}
                    <form onSubmit={isSignup ? handlePasswordSignUp : handlePasswordSignIn} className="space-y-4">
                      <Input 
                        type="email" 
                        placeholder="Email address" 
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        required 
                        disabled={loading} 
                        className="h-12 text-base" 
                      />
                      <Input 
                        type="password" 
                        placeholder={isSignup ? "Create a password (min 6 characters)" : "Password"}
                        value={password} 
                        onChange={e => setPassword(e.target.value)} 
                        required 
                        disabled={loading} 
                        className="h-12 text-base" 
                      />
                      <Button type="submit" className="w-full h-12 bg-gradient-primary text-base" disabled={loading}>
                        {loading 
                          ? (isSignup ? "Creating account..." : "Signing in...") 
                          : (isSignup ? "Create Account" : "Sign In")
                        }
                      </Button>
                    </form>

                    {/* Magic link option */}
                    <button 
                      type="button" 
                      className="w-full mt-4 text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                      onClick={() => setShowMagicLink(true)}
                    >
                      <Mail className="h-4 w-4" />
                      Prefer passwordless? Send a magic link
                    </button>
                  </>
                )}
              </>
            )}

            {step === "sent" && (
              <div className="text-center space-y-4">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  We sent a magic link to <strong>{email}</strong>
                </p>
                
                {/* Enhanced spam folder warning */}
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                    📬 Don't see the email?
                  </p>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                    <li>• Check your <strong>spam or junk folder</strong></li>
                    <li>• Look for an email from "Endless Storage"</li>
                    <li>• It may take 1-2 minutes to arrive</li>
                    <li>• Add our email to your contacts for future emails</li>
                  </ul>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  The link expires in 1 hour.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { setStep("email"); setShowMagicLink(false); }}
                >
                  ← Back to sign in
                </Button>
              </div>
            )}

            {step === "processing" && (
              <div className="text-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                <p className="text-muted-foreground">Verifying your login...</p>
                {showRetry && (
                  <div className="space-y-2 pt-4">
                    <p className="text-sm text-muted-foreground">Taking longer than expected?</p>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" onClick={() => navigate('/dashboard')}>
                        Go to Dashboard
                      </Button>
                      <Button variant="outline" onClick={() => { setStep("email"); setShowMagicLink(false); hasProcessedToken.current = false; }}>
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === "verified" && (
              <div className="text-center space-y-4">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-muted-foreground">Success! Redirecting to your dashboard...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
